(ns web3.forms.validation
  (:require [web3.utils :as utils]
            [web3.i18n :as i]))

(def validators
  "Reusable validators are defined here.

   Validators are referenced as a map of [<keyword> <& args>],
   eg. [:min-length 3].

   Validators should use the `web3.i18n` namespace for any user-facing text, passing through
   the `info` map. Where possible, include a human-readable `:label` for the field
   in the `info` map."
  {:required (fn []
               (fn [x info]
                 (when (or (nil? x)
                           (and (string? x)
                                (nil? (utils/some-str x))))
                   {:invalid (i/get :forms/validators.required info)})))

   :not-required! (constantly nil)

   :min-length
   (fn [n]
     (fn [x info]
       (when (< (count x) n)
         {:invalid (i/get :forms/validators.min-length (assoc info :n n))})))

   :max-length
   (fn [n]
     (fn [x info]
       (when (> (count x) n)
         {:invalid (i/get :forms/validators.max-length (assoc info :n n))})))

   :postal-code/north-america
   (fn []
     (fn [s _]
       (when s
         ;; regexp's from https://stackoverflow.com/questions/578406/what-is-the-ultimate-postal-code-and-zip-regex/7185241#7185241
         (let [canada #"[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d"
               usa #"\d{5}([ \-]\d{4})?"]
           (when-not (or (re-matches canada s)
                         (re-matches usa s))
             {:invalid (i/get :forms/validators.postalCode)})))))})

(defn apply-validator
  "Returns result of applying `validator` to `value`, with optional `info` map.

  Validator should be a function, or vector of the form
  [<validator-key> & args] which corresponds to a validator registered above.

  If a validator takes no arguments, it can be passed as a plain keyword.

  Validator functions should return nil, or a map containing a
  :invalid, :warn, or :info keys, each with a string value or a map containing a `:message` string."
  [validator value info]
  (let [validate (if (fn? validator) validator
                                     (let [[kw & args] (cond-> validator
                                                               (keyword? validator) (vector))
                                           make-validator (get validators kw)]
                                       (apply make-validator args)))]
    (->> (validate value info)
         (utils/map-vals #(assoc (if (string? %) {:message %} %)
                            :data {:validator validator
                                   :value value
                                   :info info})))))

(defn present?
  "`nil` and empty strings are considered 'not present'."
  [value]
  (not (nil? (cond-> value
                     (string? value) (utils/some-str)))))

(def blank-result {:invalid []
                   :warn []
                   :info []})

(defn validate-field
  "Given a value and a list of validators, return the result of validation.

  Result will be a map of the form
  {:invalid [{:message, :data}]
   :warn  [{:message, :data}]
   :info  [{:message, :data}]}

   :required is a special case. If a field is not required, and it is
   blank, we ignore the rest of the validators."
  ([validators value]
   (validate-field validators value {}))
  ([validators value info]
   (let [validators (set (map (fn [x]
                                (cond-> x
                                        (keyword? x) (vector))) validators))

         is-required? (and (contains? validators [:required])
                           (not (contains? validators [:not-required!])))
         validators (disj validators [:not-required!])
         is-present? (present? value)]
     (-> (cond (and (not is-required?) (not is-present?))
               {}

               (and is-required? (not is-present?))
               (merge-with conj blank-result (apply-validator [:required] value info))

               :else
               (->> (reduce (fn [out validator]
                              (merge-with conj out (apply-validator validator value info)))
                            blank-result
                            (remove #(= % [:required]) validators))))
         (utils/remove-empty-vals)))))

(defn validate-map
  "Given a map of the form `{<key>, { :validators, :value, & :info}, return validation results
   for all of the fields.

   example input:
   {:name {:value \"Joe\"
           :validators [:required, [:min-length 2]]
           :label \"Name\"}"
  [data]
  (reduce-kv (fn [m k {:keys [validators
                              value] :as info}]
               (assoc m k (validate-field validators value
                                          (-> info
                                              (dissoc :validators :value)))))
             {} data))

(defn invalid?
  "Returns true if validations contain an :invalid field.

   Can be called with return value of `validate-map` or `validate-field`."
  [validations]
  (or (seq (:invalid validations))
      (some? (seq (keep (comp seq :invalid) (vals validations))))))

